import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Loader2, ChevronRight } from 'lucide-react';
import { packages as mockPackages } from '../data/mock';
import { publicAPI } from '../services/api';
import { Button } from './ui/button';

const PackagesSection = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const response = await publicAPI.getPackages();
        if (response.data && response.data.length > 0) {
          setPackages(response.data);
        } else {
          setPackages(mockPackages);
        }
      } catch (error) {
        console.log('Using mock data for packages');
        setPackages(mockPackages);
      } finally {
        setLoading(false);
      }
    };
    fetchPackages();
  }, []);

  // Get packages by category
  const advancedWellnessOrder = ['SILVER PACKAGE', 'GOLD PACKAGE', 'PLATINUM PACKAGE', 'RUBY PACKAGE'];
  const advancedPackages = advancedWellnessOrder
    .map(name => packages.find(pkg => pkg.name === name))
    .filter(Boolean);
  
  const smartPackages = packages.filter(pkg => pkg.category === 'Smart Wellness Packages');

  // Category cards data
  const categories = [
    {
      id: 'advanced',
      title: 'Advanced Wellness Packages',
      description: 'Comprehensive health checkups with complete body analysis for preventive care',
      image: 'https://images.unsplash.com/photo-1659352790654-058e9077a4f4?w=600&q=70&auto=format',
      packages: advancedPackages,
      color: 'from-[#007897] to-[#00a0af]',
      imagePosition: 'center 20%',
      link: '/packages#advanced-wellness'
    },
    {
      id: 'smart',
      title: 'Smart Wellness Packages',
      description: 'Targeted health panels designed for specific health concerns and conditions',
      image: 'https://images.unsplash.com/photo-1679335026558-3e71768f0af9?w=600&q=70&auto=format',
      packages: smartPackages,
      color: 'from-[#006680] to-[#008fa3]',
      imagePosition: 'center 15%',
      link: '/packages#smart-wellness'
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-[#007897] to-[#006680]">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="text-blue-200 font-semibold text-sm uppercase tracking-wider">Health Packages</span>
          <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-4 text-white">
            Our Health Packages
          </h2>
          <p className="text-blue-100 max-w-2xl mx-auto">
            Comprehensive health check-up packages designed for preventive healthcare at affordable prices.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="animate-spin text-white" size={40} />
          </div>
        ) : (
          /* Category Selection Cards */
          <div className="grid md:grid-cols-2 gap-8 mb-8">
            {categories.map((category) => (
              <Link
                key={category.id}
                to={category.link}
                className="group cursor-pointer bg-white rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-2"
              >
                {/* Image */}
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={category.image}
                    alt={category.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    style={{ objectPosition: category.imagePosition || 'center center' }}
                    loading="lazy"
                    decoding="async"
                  />
                  <div className={`absolute inset-0 bg-gradient-to-t ${category.color} opacity-60`} />
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <h3 className="text-2xl font-bold text-white mb-1">{category.title}</h3>
                    <p className="text-blue-100 text-sm">{category.packages.length} packages available</p>
                  </div>
                </div>
                
                {/* Content */}
                <div className="p-6">
                  <p className="text-gray-600 mb-4">{category.description}</p>
                  
                  {/* Sample Package Prices */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {category.packages.slice(0, 3).map((pkg, idx) => (
                      <span key={idx} className="bg-[#007897]/10 text-[#007897] px-3 py-1 rounded-full text-sm font-medium">
                        ₹{pkg.price.toLocaleString()}
                      </span>
                    ))}
                    {category.packages.length > 3 && (
                      <span className="bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-sm">
                        +{category.packages.length - 3} more
                      </span>
                    )}
                  </div>
                  
                  <div className="flex items-center text-[#007897] font-semibold group-hover:gap-3 transition-all">
                    <span>View Packages</span>
                    <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* View All Button */}
        <div className="text-center mt-12">
          <Link to="/packages">
            <Button size="lg" className="bg-white text-[#007897] hover:bg-blue-50 px-8">
              View All Packages
              <ArrowRight size={18} className="ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default PackagesSection;
